/**
 * El idioma de quien está usando el panel.
 *
 * ── Es de la PERSONA, no del proyecto ───────────────────────────────────────
 * A diferencia de `region.tsx` —que es del proyecto abierto y decide en qué
 * moneda se cobra y en qué idioma salen las políticas—, esto viaja con quien ha
 * iniciado sesión y vale igual en todos sus proyectos. Una agencia de Miami
 * trabaja en inglés aunque el sitio que está montando cobre en pesos y mande
 * sus correos en español.
 *
 * ── Dónde se guarda ─────────────────────────────────────────────────────────
 * En la CUENTA, dentro de `user_metadata` — el mismo sitio donde ya viven el
 * nombre y la foto. Sin tabla nueva y sin migración: Supabase Auth lo guarda y
 * lo devuelve dentro de la sesión, así que también viaja en el token y el
 * worker puede leerlo para los correos que van dirigidos a esta persona.
 *
 * El navegador guarda una copia, y no por duplicar: es lo único que hay en el
 * primer pintado, antes de que la sesión termine de cargar. Sin ella, quien
 * tiene el panel en inglés vería un parpadeo en español en cada recarga.
 *
 * Esa copia la lleva `@webbuilder/i18n` —cookie de `.veltica.com` más
 * `localStorage`—, no este archivo. Es lo que hace que cruzar del panel al
 * editor no cambie de idioma: `localStorage` no cruza subdominios y la cookie
 * sí. La misma mecánica que el color de acento, y por el mismo motivo.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  CATALOGS,
  DEFAULT_LANGUAGE,
  createTranslator,
  formatters,
  isLanguage,
  persistLanguage,
  readLanguage,
  type Formatters,
  type Language,
  type Translate,
} from '@webbuilder/i18n'
import { serverErrorCode, serverErrorVars, serverMessage } from './api'
import { supabase } from './supabase'

/**
 * El idioma vigente fuera de React.
 *
 * Mismo motivo que `moneyLocale` en `region.tsx`: hay funciones sueltas —el
 * traductor de errores del servidor, formateadores en celdas de tabla— que no
 * son componentes y no pueden usar hooks. Vale porque sólo hay una persona
 * usando el panel a la vez.
 */
let currentLanguage: Language = DEFAULT_LANGUAGE

export function activeLanguage(): Language {
  return currentLanguage
}

type LanguageValue = {
  language: Language
  setLanguage: (language: Language) => void
  t: Translate
  fmt: Formatters
}

const LanguageContext = createContext<LanguageValue | null>(null)

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const initial = readLanguage()
    currentLanguage = initial
    return initial
  })

  const setLanguage = useCallback((next: Language) => {
    currentLanguage = next
    setLanguageState(next)
    persistLanguage(next)
    /**
     * Y en la cuenta, para que siga a esta persona a otro ordenador.
     *
     * Sin esperar a que termine y sin avisar si falla: la interfaz ya cambió y
     * quedó guardada en el navegador. Un error aquí significa que el cambio no
     * viajará a otro dispositivo, no que no haya ocurrido — y un aviso de
     * fallo sobre algo que la persona VE que funcionó sólo confunde.
     */
    void supabase.auth.getSession().then(({ data }) => {
      if (!data.session) return
      const meta = (data.session.user.user_metadata ?? {}) as Record<string, unknown>
      if (meta['language'] === next) return
      return supabase.auth.updateUser({ data: { ...meta, language: next } })
    }).catch(() => undefined)
  }, [])

  /**
   * Al cargar la sesión, manda lo que diga la cuenta.
   *
   * El arranque sale del navegador porque es lo único disponible en el primer
   * pintado. En cuanto se sabe quién ha entrado, su preferencia guardada gana:
   * es la que eligió, aunque este ordenador tenga otra cosa en memoria.
   */
  useEffect(() => {
    let alive = true
    void supabase.auth.getSession().then(({ data }) => {
      const stored = (data.session?.user.user_metadata as { language?: unknown } | undefined)?.language
      if (alive && isLanguage(stored) && stored !== currentLanguage) setLanguage(stored)
    }).catch(() => undefined)
    return () => { alive = false }
  }, [setLanguage])

  /* El atributo del documento, para que el navegador sepa en qué idioma está:
     de ahí salen la partición de palabras, el corrector y lo que anuncia un
     lector de pantalla. */
  useEffect(() => {
    document.documentElement.lang = language
  }, [language])

  const value = useMemo<LanguageValue>(() => ({
    language,
    setLanguage,
    t: createTranslator(language, CATALOGS, (key) => {
      /* Ruidoso a propósito, y sólo en desarrollo: una clave que falta se ve en
         pantalla —se pinta la clave— pero al que la ve le falta saber dónde
         mirar. En producción no se avisa: no es un error del usuario. */
      if (import.meta.env.DEV) console.warn(`[i18n] falta la clave «${key}»`)
    }),
    fmt: formatters(language),
  }), [language, setLanguage])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

/**
 * El error del servidor, dicho en el idioma de quien mira.
 *
 * Prefiere el CÓDIGO al texto: el worker manda los dos, y el texto viene
 * siempre en español. Cuando no hay código traducible —una ruta que aún no lo
 * manda— cae al texto del servidor, que se entiende aunque esté en el otro
 * idioma; y si tampoco hay, a la frase que pase quien llama.
 *
 * Es una función y no un hook porque se llama desde `catch`, donde no hay
 * hooks: lee el idioma vigente de la variable de módulo, igual que `formatMoney`.
 */
/**
 * El traductor FUERA de un componente.
 *
 * Mismo motivo que `activeLanguage`: hay funciones sueltas —los extractores de
 * errores, formateadores de celda— que no son componentes y no pueden usar
 * hooks. Se construye en cada llamada a propósito: el idioma puede haber
 * cambiado desde la última, y un traductor guardado se quedaría en el anterior.
 */
export function translate(key: string, vars?: Record<string, string | number>): string {
  return createTranslator(currentLanguage, CATALOGS)(key, vars)
}

export function translateServerError(error: unknown, fallback: string): string {
  const code = serverErrorCode(error)
  const t = createTranslator(currentLanguage, CATALOGS)
  if (code) {
    const key = `error.${code}`
    /* Con lo que mandó el servidor para rellenar los huecos. Sin ellos, un
       texto como «{limit} invitados» se enseñaba con la llave a la vista. */
    const translated = t(key, serverErrorVars(error))
    /* `t` devuelve la clave cuando no la conoce: eso significa que el servidor
       mandó un código que este panel todavía no sabe decir, y entonces vale más
       su texto que un identificador en pantalla. */
    if (translated !== key) return translated
  }
  return serverMessage(error, fallback)
}

/**
 * El traductor y los formateadores de la pantalla.
 *
 * Sin proveedor devuelve un traductor del idioma por defecto en vez de lanzar:
 * hay pantallas que se montan sueltas en pruebas, y reventar ahí convierte un
 * texto en un fallo de arranque.
 */
export function useI18n(): LanguageValue {
  const value = useContext(LanguageContext)
  return useMemo<LanguageValue>(() => value ?? {
    language: DEFAULT_LANGUAGE,
    setLanguage: () => undefined,
    t: createTranslator(DEFAULT_LANGUAGE, CATALOGS),
    fmt: formatters(DEFAULT_LANGUAGE),
  }, [value])
}
